/**
 * Slice 19b — Per-template sign-off control for the Library panel.
 *
 * States rendered (mutually exclusive):
 *   - Already signed off → "Signed off" chip (+ "override" badge with the reason as title when forced) + Revoke.
 *   - Not yet signed off, current >= required → enabled "Sign off pattern".
 *   - Not yet signed off, current  < required → disabled "Sign off" hint + collapsible "Override (trainer)"
 *     with a required reason textarea (max 500) and "Force sign-off" button.
 *
 * Progress numbers are only present when the templates list was loaded with
 * `?include=signoff_progress` (server-side in [`adsTemplates.ts`](../../../server/admin-handlers/adsTemplates.ts)).
 */
import { useState } from "react";
import { updateAdsTemplate, type AdsAssistantTemplateRow } from "@/lib/adsAssistantClient";

const SIGNOFF_REASON_MAX = 500;

type Props = {
  template: AdsAssistantTemplateRow;
  canEdit: boolean;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  onChanged: () => Promise<void> | void;
};

export function AdsLibraryTemplateSignoffPanel({ template, canEdit, setError, setBanner, onChanged }: Props) {
  const t = template;
  const current = typeof t.linked_post_count === "number" ? t.linked_post_count : null;
  const required = typeof t.signoff_required_min === "number" ? t.signoff_required_min : null;
  const isSignedOff = !!t.trainer_pattern_signed_off;
  const forcedReason = (t.trainer_pattern_signed_off_forced_reason ?? "").trim();
  const hasProgress = current !== null && required !== null;
  const meetsThreshold = hasProgress && current! >= required!;

  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  async function signOff(args: { force: boolean; reason?: string }) {
    setError(null);
    setBusy(true);
    const r = await updateAdsTemplate(t.id, {
      trainer_pattern_signed_off: true,
      ...(args.force ? { force: true, signoff_reason: args.reason ?? "" } : {}),
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setReason("");
    setBanner(args.force ? "Template signed off via override." : "Template signed off.");
    await onChanged();
  }

  async function revoke() {
    setError(null);
    setBusy(true);
    const r = await updateAdsTemplate(t.id, { trainer_pattern_signed_off: false });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setBanner("Sign-off revoked.");
    await onChanged();
  }

  if (isSignedOff) {
    return (
      <span className="flex flex-wrap items-center gap-2 shrink-0">
        <span
          className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1 py-0.5 text-[10px] font-semibold text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100"
        >
          Signed off
          {forcedReason ? (
            <span
              className="rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-950/60 dark:text-amber-100"
              title={`Override reason: ${forcedReason}`}
            >
              override
            </span>
          ) : null}
        </span>
        {canEdit ? (
          <button
            type="button"
            className="text-[10px] text-gray-700 hover:underline dark:text-gray-300"
            disabled={busy}
            onClick={() => void revoke()}
          >
            {busy ? "Working…" : "Revoke sign-off"}
          </button>
        ) : null}
      </span>
    );
  }

  if (!canEdit) {
    return hasProgress ? (
      <span className="text-[10px] text-gray-500 dark:text-gray-400">
        Linked posts: {current} / {required}
      </span>
    ) : null;
  }

  return (
    <span className="flex flex-wrap items-center gap-2 shrink-0">
      {hasProgress ? (
        <span
          className={
            "rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums " +
            (meetsThreshold
              ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100"
              : "bg-stone-100 text-stone-800 dark:bg-zinc-800 dark:text-gray-200")
          }
          title="Posts linked to this template (badge or curated examples) / required minimum for sign-off"
        >
          Linked posts: {current} / {required}
        </span>
      ) : null}
      {meetsThreshold ? (
        <button
          type="button"
          className="text-[10px] font-medium text-emerald-800 hover:underline disabled:opacity-50 dark:text-emerald-300"
          disabled={busy}
          onClick={() => void signOff({ force: false })}
        >
          {busy ? "Signing off…" : "Sign off pattern"}
        </button>
      ) : (
        <details className="text-[10px] text-gray-700 dark:text-gray-300">
          <summary className="cursor-pointer font-medium text-amber-800 hover:underline dark:text-amber-300 [&::-webkit-details-marker]:hidden">
            Override (trainer) ▾
          </summary>
          <div className="mt-1.5 flex w-full flex-col gap-1.5 sm:w-80">
            <label className="text-[10px] text-gray-600 dark:text-gray-400">
              Reason (required, ≤{SIGNOFF_REASON_MAX} chars)
            </label>
            <textarea
              className="w-full rounded border border-amber-300 bg-white px-2 py-1 text-[11px] dark:border-amber-700 dark:bg-zinc-900 dark:text-gray-100"
              value={reason}
              maxLength={SIGNOFF_REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Only one strong exemplar this quarter; signing off to unblock pilot."
              disabled={busy}
              rows={2}
            />
            <button
              type="button"
              className="self-start rounded border border-amber-700 px-1.5 py-1 text-[10px] font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-950/40"
              disabled={busy || !reason.trim()}
              onClick={() => void signOff({ force: true, reason: reason.trim() })}
            >
              {busy ? "Forcing…" : "Force sign-off"}
            </button>
          </div>
        </details>
      )}
    </span>
  );
}
